"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const models_1 = require("../models");
const Observable_1 = require("../socket/Observable");
const socket_1 = require("../socket");
const NegativeUnreadCountException_1 = require("../utils/NegativeUnreadCountException");
const DomainEvents_1 = require("../utils/DomainEvents");
class OrgStorage {
    constructor(organizationId) {
        this.reportedCounters = new Set();
        this.organizationId = organizationId;
        this.counters = (0, models_1.initialCounterData)();
    }
    setMessageCounterByType(type, count) {
        this.counters.message[type] = count;
        this.counters.messageCounter = this.deriveMessageCounter();
        this.notifyCounters();
    }
    getCounters() {
        return this.counters;
    }
    setTotalAlertCount(count) {
        this.counters.alertCounter = count;
        this.notifyCounters();
    }
    setAlertsCount(priorityCount, acknowledgeCount, sentAcknowledgmentCount, referralCount) {
        this.counters.alert[models_1.ALERT_TYPE.PRIORITY] = priorityCount;
        this.counters.alert[models_1.ALERT_TYPE.ACKNOWLEDGE] = acknowledgeCount;
        this.counters.alert[models_1.ALERT_TYPE.SENT_ACKNOWLEDGEMENT] =
            sentAcknowledgmentCount;
        this.counters.alert[models_1.ALERT_TYPE.REFERRAL] = referralCount;
        this.counters.alertCounter = this.deriveAlertCounter();
        this.notifyCounters();
    }
    updateAlertCountByType(type, isIncrement, removedAlertCount = 1) {
        if (isIncrement) {
            this.counters.alert[type]++;
        }
        else {
            this.counters.alert[type] = this.counters.alert[type] - removedAlertCount;
        }
        this.counters.alertCounter = this.deriveAlertCounter();
        this.notifyCounters();
    }
    setStarredMessageCount(starredMessageCount) {
        this.counters.starredMessageCounter = starredMessageCount;
        this.notifyCounters();
    }
    reset() {
        this.counters = (0, models_1.initialCounterData)();
    }
    deriveMessageCounter() {
        return (this.counters.message[models_1.GROUP_TYPE.PATIENT] +
            this.counters.message[models_1.GROUP_TYPE.NON_PATIENT] +
            this.counters.message[models_1.GROUP_TYPE.PRIVATE] +
            this.counters.message[models_1.GROUP_TYPE.BROADCAST]);
    }
    deriveAlertCounter() {
        return (this.counters.alert[models_1.ALERT_TYPE.PRIORITY] +
            this.counters.alert[models_1.ALERT_TYPE.ACKNOWLEDGE] +
            this.counters.alert[models_1.ALERT_TYPE.REFERRAL]);
    }
    notifyCounters() {
        this.sanitizeCounters();
        Observable_1.Observable.getInstance().notify((0, DomainEvents_1.getEventKey)(socket_1.DomainEvents.ORG_COUNTERS, this.organizationId), this.getCounters());
    }
    // These counters are a running tally driven by socket events and are only
    // re-baselined from the server at org init and on socket re-auth, so a single
    // miscounted event persists for the rest of the session. Once the tally
    // crosses zero the org badge renders the negative verbatim, because
    // OrganizationStyledBadge treats any non-zero value as visible.
    //
    // Clamp rather than throw: Observable.notify swallows observer errors into a
    // console.error of its own, so an exception raised on this socket-driven path
    // would never reach the app's ErrorBoundary and would group in Sentry under
    // the Observable message instead. console.error is the seam that reaches
    // Sentry - captureConsoleIntegration({levels:["error"]}) is wired on both web
    // and mobile.
    sanitizeCounters() {
        let repairedType = false;
        Object.keys(this.counters.message).forEach(type => {
            const safeCount = this.clamp(this.counters.message[type], `message.${type}`);
            repairedType = repairedType || safeCount !== this.counters.message[type];
            this.counters.message[type] = safeCount;
        });
        Object.keys(this.counters.alert).forEach(type => {
            const safeCount = this.clamp(this.counters.alert[type], `alert.${type}`);
            repairedType = repairedType || safeCount !== this.counters.alert[type];
            this.counters.alert[type] = safeCount;
        });
        // Re-derive before clamping the totals: a total computed from a type we
        // just repaired is stale, and clamping it on its own would leave the badge
        // disagreeing with the list it summarises.
        if (repairedType) {
            this.counters.messageCounter = this.deriveMessageCounter();
            this.counters.alertCounter = this.deriveAlertCounter();
        }
        this.counters.messageCounter = this.clamp(this.counters.messageCounter, "messageCounter");
        this.counters.alertCounter = this.clamp(this.counters.alertCounter, "alertCounter");
        this.counters.starredMessageCounter = this.clamp(this.counters.starredMessageCounter, "starredMessageCounter");
    }
    clamp(count, counter) {
        if (Number.isFinite(count) && count >= 0) {
            return count;
        }
        this.report(count, counter);
        return 0;
    }
    // Once a counter has drifted it keeps drifting, so every later socket event
    // would re-report the same desync. One report per counter per session is
    // enough to diagnose it: the [DEBUG socket] warns ride along as breadcrumbs
    // and carry the event sequence that produced it.
    report(count, counter) {
        if (this.reportedCounters.has(counter)) {
            return;
        }
        this.reportedCounters.add(counter);
        const details = {
            message: `NegativeUnreadCountException: ${counter} cannot be negative | organizationId: ${this.organizationId} | value: ${count}`,
            organizationId: this.organizationId,
            counter,
            value: count,
            counters: JSON.parse(JSON.stringify(this.counters)),
        };
        console.error(details.message, new NegativeUnreadCountException_1.NegativeUnreadCountException(details));
    }
}
exports.default = OrgStorage;
